/*
 * album/grid.ts - the Grid view (GALLERY_PLAN.md §5, AL6): chapter chips with counts (All + every shown chapter +
 * 更多照片), source chips (All / Photographer / Guests - the counts on the chapter chips follow the source),
 * sub-group chips while one chapter with sub-groups is selected, and the dense grid itself - pages of 60 from
 * GET /Gallery/Photos, the next page fetched as the sentinel under the grid nears the screen (a "Load more"
 * button stands in without IntersectionObserver or after a failed page). Tiles open the shared viewer over the
 * loaded set, which fetches further pages itself at the end (ViewerSource). Each tile carries a ♥ (D14).
 */

import { AlbumData, Chapter, Favorites, Photo, bindHearts, cell, chapterOf, fetchPhotos, fmt, toast } from "./shared";
import { Viewer, ViewerSource } from "./viewer";

const PAGE = 60;
/** Start fetching the next page this far before the sentinel scrolls into view. */
const AHEAD = "0px 0px 900px 0px";

export type Source = "all" | "pro" | "guest";

export interface GridFilter {
  /** Chapter slug (UNGROUPED = 更多照片), null = every photo. */
  group: string | null;
  /** Sub-group ID within the chapter, or null. */
  sub: number | null;
  src: Source;
}

const TRIP = 3;
const TIMELINE = 1;

export function parseSource(s: string | null | undefined): Source {
  return s === "pro" || s === "guest" ? s : "all";
}

export class Grid {
  items: Photo[] = [];
  /** Called when a chip changes the filter (index.ts keeps the URL in step). */
  onFilter: (f: GridFilter) => void = () => {};

  private f: GridFilter = { group: null, sub: null, src: "all" };
  private total = 0;
  private loaded = false;
  private failed = false;
  private gen = 0;
  private busy: Promise<boolean> | null = null;
  private empty: HTMLElement | null = null;
  private readonly host: HTMLElement;
  private readonly more: HTMLButtonElement | null;
  private readonly sentinel: HTMLElement | null;
  private readonly chapterChips: HTMLButtonElement[];
  private readonly srcChips: HTMLButtonElement[];
  private readonly subBar: HTMLElement | null;
  private readonly totalEl: HTMLElement | null;
  private readonly io: IntersectionObserver | null = null;
  private readonly source: ViewerSource = { more: () => this.load(), total: () => this.total };

  constructor(section: HTMLElement, private readonly data: AlbumData, private readonly favs: Favorites, private readonly viewer: Viewer) {
    this.host = section.querySelector<HTMLElement>("[data-album-grid]")!;
    this.more = section.querySelector<HTMLButtonElement>("[data-album-grid-more]");
    this.sentinel = section.querySelector<HTMLElement>("[data-album-grid-sentinel]");
    this.chapterChips = Array.from(section.querySelectorAll<HTMLButtonElement>("[data-grid-chapters] [data-g]"));
    this.srcChips = Array.from(section.querySelectorAll<HTMLButtonElement>("[data-grid-src] [data-src]"));
    this.subBar = section.querySelector<HTMLElement>("[data-grid-subs]");
    this.totalEl = section.querySelector<HTMLElement>("[data-grid-total]");

    this.more?.addEventListener("click", () => void this.load());

    this.chapterChips.forEach((b) =>
      b.addEventListener("click", () => this.apply({ group: b.dataset.g || null, sub: null, src: this.f.src })),
    );
    this.srcChips.forEach((b) =>
      b.addEventListener("click", () => this.apply({ ...this.f, src: parseSource(b.dataset.src) })),
    );
    this.subBar?.addEventListener("click", (e) => {
      const b = (e.target as HTMLElement).closest<HTMLButtonElement>("[data-sg]");
      if (b) this.apply({ ...this.f, sub: b.dataset.sg ? Number(b.dataset.sg) : null });
    });

    // tiles -> the viewer over everything loaded so far (it fetches more itself); handled before the page-wide
    // [data-album-list] listener in story.ts, which then sees defaultPrevented
    this.host.addEventListener("click", (e) => {
      const me = e as MouseEvent;
      if (me.defaultPrevented || me.button !== 0 || me.ctrlKey || me.metaKey || me.shiftKey || me.altKey) return;
      const a = (e.target as HTMLElement).closest<HTMLAnchorElement>("a[data-photo]");
      if (!a) return;
      const i = this.items.findIndex((p) => p.id === a.dataset.photo);
      if (i < 0) return;
      e.preventDefault();
      this.viewer.open(this.items, i, this.source);
    });
    bindHearts(this.host, favs, data);

    if (this.sentinel && "IntersectionObserver" in window) {
      this.io = new IntersectionObserver((entries) => {
        if (entries.some((en) => en.isIntersecting) && this.hasMore() && !this.failed) void this.load();
      }, { rootMargin: AHEAD });
      this.io.observe(this.sentinel);
    }
  }

  get filter(): GridFilter {
    return { ...this.f };
  }

  /**
   * Shows the grid for a filter; reloads only when it changed. An unknown chapter / sub-group falls back silently
   * (a stale or hand-edited URL).
   */
  async show(group: string | null, sub: number | null = null, src: Source = "all"): Promise<void> {
    const g = group && this.chapterChips.some((b) => b.dataset.g === group) ? group : null;
    const ch = g ? chapterOf(this.data, g) : undefined;
    const s = sub != null && ch?.subgroups.some((x) => x.id === sub) ? sub : null;
    const next: GridFilter = { group: g, sub: s, src };
    if (this.loaded && next.group === this.f.group && next.sub === this.f.sub && next.src === this.f.src) {
      await (this.busy ?? Promise.resolve(false));
      return;
    }
    this.f = next;
    this.gen++;
    this.busy = null;
    this.failed = false;
    this.items = [];
    this.total = 0;
    this.loaded = true;
    this.host.replaceChildren();
    this.empty?.remove();
    this.empty = null;
    this.paintChips();
    await this.load();
  }

  /** Opens the viewer on one photo of the grid (a deep link, D15); a photo not in it goes first. */
  openAt(p: Photo): void {
    const i = this.items.findIndex((x) => x.id === p.id);
    if (i >= 0) this.viewer.open(this.items, i, this.source);
    else this.viewer.open([p, ...this.items], 0);
  }

  private apply(f: GridFilter): void {
    void this.show(f.group, f.sub, f.src).then(() => this.onFilter(this.filter));
  }

  private hasMore(): boolean {
    return this.loaded && this.items.length < this.total;
  }

  /** The next page; resolves true when photos were added. */
  private load(): Promise<boolean> {
    if (this.busy) return this.busy;
    const gen = this.gen;
    const f = this.f;
    const busy = (async (): Promise<boolean> => {
      if (this.more) this.more.disabled = true;
      let added = false;
      try {
        const page = await fetchPhotos({ g: f.group, sg: f.sub, src: f.src === "all" ? undefined : f.src, skip: this.items.length, take: PAGE });
        if (gen !== this.gen) return false;
        this.total = page.total;
        const frag = document.createDocumentFragment();
        page.items.forEach((p) => {
          this.data.photos[p.id] = this.data.photos[p.id] ?? p;
          this.items.push(p);
          frag.appendChild(cell(p, this.favs, this.data, "album-tile--masonry"));
        });
        this.host.appendChild(frag);
        added = page.items.length > 0;
        if (!added) this.total = this.items.length;   // nothing came back: stop asking
        this.failed = false;
        if (!this.items.length && !this.empty) {
          this.empty = document.createElement("p");
          this.empty.className = "album-gridview__empty";
          this.empty.textContent = this.data.i18n.gridEmpty;
          this.host.after(this.empty);
        }
      } catch {
        if (gen !== this.gen) return false;
        this.failed = true;
        toast(this.data.i18n.loadFail);
      } finally {
        if (gen === this.gen) {
          this.busy = null;
          this.paintMore();
        }
      }
      // a tall screen can still show the sentinel after a page: keep filling it
      if (added && gen === this.gen) requestAnimationFrame(() => this.fillScreen());
      return added;
    })();
    this.busy = busy;
    return busy;
  }

  private fillScreen(): void {
    if (!this.io || !this.sentinel || !this.hasMore() || this.failed || this.busy) return;
    if (this.sentinel.offsetParent === null) return;   // the grid is hidden (Story view)
    const r = this.sentinel.getBoundingClientRect();
    if (r.top < window.innerHeight + 900) void this.load();
  }

  private paintMore(): void {
    if (this.more) {
      this.more.disabled = false;
      // the button only stands in for the sentinel: no IntersectionObserver, or a page that failed
      this.more.hidden = !this.hasMore() || (!!this.io && !this.failed);
    }
    if (this.totalEl) this.totalEl.textContent = this.loaded ? fmt(this.data.i18n.gridCount, this.total) : "";
  }

  private count(pro: number, guest: number): number {
    return this.f.src === "pro" ? pro : this.f.src === "guest" ? guest : pro + guest;
  }

  private paintChips(): void {
    const press = (b: HTMLButtonElement, on: boolean): void => {
      b.classList.toggle("is-active", on);
      b.setAttribute("aria-pressed", on ? "true" : "false");
    };

    this.chapterChips.forEach((b) => {
      const g = b.dataset.g || null;
      const n = this.count(Number(b.dataset.pro ?? 0), Number(b.dataset.guest ?? 0));
      const on = g === this.f.group;
      press(b, on);
      const num = b.querySelector<HTMLElement>("[data-n]");
      if (num) num.textContent = String(n);
      b.hidden = g !== null && n === 0 && !on;   // e.g. Guests: chapters nobody sent photos of
    });
    this.srcChips.forEach((b) => press(b, b.dataset.src === this.f.src));

    // sub-group chips: one chapter with sub-groups (a Trip chapter's are 📍 places, a Timeline's carry the time)
    if (!this.subBar) return;
    const ch: Chapter | undefined = this.f.group ? chapterOf(this.data, this.f.group) : undefined;
    const subs = ch?.subgroups ?? [];
    this.subBar.replaceChildren();
    this.subBar.hidden = !ch || subs.length === 0;
    if (!ch || !subs.length) return;
    const chip = (sg: number | null, label: string, n: number, pin: boolean): HTMLButtonElement => {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "album-chip";
      b.dataset.sg = sg == null ? "" : String(sg);
      if (pin) {
        const i = document.createElement("span");
        i.className = "fa-solid fa-location-dot album-chip__pin";
        i.setAttribute("aria-hidden", "true");
        b.append(i, " ");
      }
      b.append(label + " ");
      const c = document.createElement("span");
      c.className = "album-chip__n";
      c.textContent = String(n);
      b.appendChild(c);
      press(b, sg === this.f.sub);
      b.hidden = sg !== null && n === 0 && sg !== this.f.sub;
      return b;
    };
    this.subBar.appendChild(chip(null, this.data.i18n.gridAll, this.count(ch.total, ch.guestCount), false));
    subs.forEach((s) => {
      const label = (ch.display === TIMELINE && s.time ? `${s.time} ` : "") + (s.name ?? "");
      this.subBar!.appendChild(chip(s.id, label, this.count(s.total, s.guests), ch.display === TRIP));
    });
  }
}
